import axios from "axios";
import { Box, Link } from "@mui/material";
import {
  getClearGeneralRequest,
  getUserPersonalId,
} from "../../../common/EntitiesManagement";
import useGlobalContext from "../../../hooks/useGlobalContext";
import ErrorMessage from "../../../components/common/ErrorMessage";
import localize from "../../../common/localize";
import TableComponent from "../../../components/common/TableComponent";
import PagePagination from "../../../components/PagePagination";
import WarningErrorStatus from "../../../components/common/WarningErrorStatus";
import ActionButtons from "../../../components/common/ActionButtons";
import { BackForeColor } from "../../../components/common/BackForeColor";
import RequestStatus from "../../../components/common/RequestStatus";
import {
  generateNoDataValues,
  getCurrentDays,
  GetDateInDDMMYYYYFormat,
  getDaysToError,
  getDaysToWarning,
  PaginationLimit,
} from "../../../common/utils";
import { useEffect, useState } from "react";
import LoadingPanel from "../../../components/common/LoadingPanel";
import ModalBackground from "../../../components/common/ModalBackground";
import AddOrEditGeneralRequests from "./AddOrEditGeneralRequests";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";

export default function RequestsAssignedToMe({ filters }) {
  const {
    config,
    pageIndexRequestAssignedToMe,
    setPageIndexRequestAssignedToMe,
    GENERAL_REQUESTS_ENDPOINT,
  } = useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);
  const [myRequestsData, setMyRequetsData] = useState([]);
  const [numberOfPages, setNumberOfPages] = useState(1);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [requestModel, setRequestModel] = useState(null);

  const getRequestsAssignedToMe = async () => {
    try {
      setIsLoading(true);

      const response = await axios.get(
        `${GENERAL_REQUESTS_ENDPOINT}/personal/${getUserPersonalId()}`,
        {
          ...config,
          headers: {
            ...config.headers,
            pagination: pageIndexRequestAssignedToMe,
            Filter: JSON.stringify(filters),
          },
        }
      );

      if (response?.data?.Ok && response?.data?.Data?.length > 0) {
        const pages = response?.data?.Count / PaginationLimit();
        setNumberOfPages(pages > 0 ? pages : 1);
        setMyRequetsData(response.data.Data);
      }
    } catch (exception) {
      setErrorMessage(exception.message);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const showUI = () => {
    return (
      <Box className="flex flex-col gap-2 w-full">
        {showErrorModal && (
          <ErrorMessage
            message={errorMessage}
            title={localize.error_unexpected}
            showModal={showErrorModal}
            setShowModal={setShowErrorModal}
          />
        )}

        {showAddModal && (
          <ModalBackground
            isDrawerMode
            hasRequiredValues={requestModel?.Id === 0}
            title={`${localize.request} #${requestModel.Id}`}
            showModal={showAddModal}
            setShowModal={setShowAddModal}
            onClickToCancel={() => {
              setRequestModel(getClearGeneralRequest());
              setShowAddModal(false);
            }}
            cancelButtonText={localize.close}
            hideSubmitButton
            Component={
              <AddOrEditGeneralRequests
                model={requestModel}
                setModel={setRequestModel}
                readOnly
              />
            }
          />
        )}

        <TableComponent
          firstColumnTitle={" "}
          noPadding
          pagination={
            <PagePagination
              pageIndex={pageIndexRequestAssignedToMe}
              setPageIndex={setPageIndexRequestAssignedToMe}
              dataArray={myRequestsData}
              customNumberOfPages={numberOfPages}
            />
          }
          columnTitles={[
            localize.actions,
            localize.type,
            localize.status,
            localize.event,
            localize.client,
            localize.expected_attention_date,
            localize.creation_date,
            localize.created_by,
          ]}
          results={
            myRequestsData?.length > 0
              ? myRequestsData.map((item) => ({
                  key: item.Id,
                  data: [
                    <WarningErrorStatus
                      currentValue={getCurrentDays(item)}
                      limitForWarning={getDaysToWarning(item)}
                      limitForError={getDaysToError(item)}
                      isSucceeded={item?.Status === 2}
                      isFailed={item?.Status === 3}
                    />,
                    <div>
                      <ActionButtons
                        onClickForDetailButton={() => {
                          setRequestModel({
                            ...item,
                            RequestTypeId: item?.Type?.Id,
                            ClientId: item.Client?.Id,
                            PersonalId: item?.PersonalAssigned?.Id,
                            ExpectedSolutionDateValue: new Date(
                              item?.ExpectedSolutionDate
                            ),
                            EventId: item?.Event?.Id ?? 0,
                          });
                          setShowAddModal(true);
                        }}
                        hideDeleteButton
                        hideEditButton
                        detailsButtonText={localize.details}
                      />
                    </div>,
                    <BackForeColor
                      backColor={item?.Type?.BackgroundColor}
                      foreColor={item?.Type?.ForegroundColor}
                      text={item?.Type?.Name}
                    />,
                    <RequestStatus status={item.Status} />,
                    item.Event ? (
                      <Link
                        color="primary"
                        underline="always"
                        href="#"
                        onClick={() => {
                          window.open(
                            `/events/detail/${item.Event.Id}`,
                            "_blank"
                          );
                        }}
                      >{`${item.Event.Code}`}</Link>
                    ) : (
                      <ValueOrNoDataLabel />
                    ),
                    <ValueOrNoDataLabel data={item?.Client?.Name} />,
                    GetDateInDDMMYYYYFormat(
                      new Date(item?.ExpectedSolutionDate)
                    ),
                    new Date(item?.CreationDate).toLocaleString(undefined, {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }),
                    <ValueOrNoDataLabel data={item?.CreatedBy} />,
                  ],
                }))
              : [generateNoDataValues(9)]
          }
        />
      </Box>
    );
  };

  useEffect(() => {
    getRequestsAssignedToMe();
  }, [filters]);

  return isLoading ? <LoadingPanel donotUseHscreen /> : showUI();
}
